/**
 * After a live, as the confirmed prototype draws it (g-front, "after the live"): thank you, the replay (being
 * prepared, then it plays right here, with where people reacted along its bar and the busiest moments marked), the
 * seven reactions, the closed chat with a way to the host, the offer from tonight, the next session of a series,
 * and a word to the host.
 *
 * The replay's reactions are today's (`useReplayReactionCore`): taps join the strip at once and go to the API in
 * batches; the live's reactions rise again as the replay plays across them.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { REACTIONS, type ReactionTimeline } from "../../../shared/live-reactions";
import type { LiveCalendarLinks, OfferJson, PublicBrand, WatchSession } from "../../../shared/types";
import { addCounts, byCount, REACTION_OF } from "../../live/room/reactions";
import { api } from "../../lib/api";
import { fmt } from "../../lib/when";
import type { Arrivals } from "../TalkPlayer";
import { PLAY_BEFORE_SEC, type ReplayTap } from "../replayMaths";
import { useReplayReactionCore } from "../ReplayReactions";
import { Brand, Made, Sheet, Toast, useToast } from "../room/chrome";
import { GIcon } from "../room/GIcon";
import { RoomChat } from "../room/RoomChat";
import { OfferPress, offerPill } from "../room/RoomOffer";
import { mmss } from "../room/talk";
import { useRoomVideo } from "../room/useRoomVideo";
import { useWatchProgress } from "../watchProgress";
import type { WatchThread } from "../WatchPageView";
import { talkingWords, useKeepTalking } from "./keepTalking";
import { Floats } from "./liveParts";
import { CalendarButtons } from "./LiveEarly";

/** How many bars the reactions strip draws over the replay's bar. */
const STRIP_BARS = 96;
/** How many of the busiest moments carry a mark. */
const PINS = 3;

/** The replay holds no moments of its own: the live's were answered in the room. */
const NO_ARRIVALS: Arrivals = { up: null, waiting: 0, paused: false, onTime: () => {}, dismiss: () => {} };

/** The strip's bars, in % of its height: the live's and the replay's taps together, and whether the replay's lead. */
export function stripBars(live: number[], replay: number[], bars = STRIP_BARS): { h: number; pale: boolean }[] {
  const bins = Math.max(live.length, replay.length);
  if (bins === 0) return [];
  const group = Math.max(1, Math.ceil(bins / bars));
  const out: { a: number; b: number }[] = [];
  for (let i = 0; i < bins; i += group) {
    let a = 0;
    let b = 0;
    for (let j = i; j < Math.min(bins, i + group); j++) {
      a += live[j] ?? 0;
      b += replay[j] ?? 0;
    }
    out.push({ a, b });
  }
  const max = Math.max(1, ...out.map((x) => x.a + x.b));
  return out.map(({ a, b }) => ({ h: Math.round(8 + 92 * ((a + b) / max) ** 0.7), pale: b > a }));
}

interface NextSession {
  weekday: string;
  day: string;
  /** "Next Tuesday · 6:00 PM ET". */
  when: string;
  /** "Session 11: Getting your home photo-ready". */
  title: string;
}

interface AfterProps {
  brand: PublicBrand;
  token: string;
  /** The talk the replay plays from, once it plays. */
  talk: WatchSession;
  replay: "none" | "preparing" | "ready";
  reactions: ReactionTimeline | undefined;
  /** A series' session, whose replay place and reactions are kept on it. */
  sessionId?: string;
  eyebrow: string;
  /** The room was being recorded while this person was in it, so a replay is on its way even before our state says so. */
  recorded: boolean;
  viewerFirst: string;
  viewerEmail: string;
  hostFirst: string;
  offer: OfferJson | null;
  onConvert: () => void;
  next: NextSession | null;
  calendar: LiveCalendarLinks | undefined;
  thread: WatchThread;
  /** Set after a circle's call: Keep talking opens that circle. */
  circleId?: string;
  yourPage: string;
  tick: (step: { to: number; playing: boolean }) => void;
}

function Replay(p: AfterProps): ReactNode {
  const { talk, token, sessionId } = p;
  const report = useWatchProgress(token, talk.webinar_id, () => {}, sessionId);
  const v = useRoomVideo({ session: talk, arrivals: NO_ARRIVALS, onProgress: report });
  const send = useCallback(
    (taps: ReplayTap[], leaving: boolean) =>
      api.watch.reactions(token, { webinar_id: talk.webinar_id, ...(sessionId ? { session_id: sessionId } : {}), taps }, leaving),
    [token, talk.webinar_id, sessionId],
  );
  const drawn = useMemo(() => (p.reactions ? { timeline: p.reactions, send } : undefined), [p.reactions, send]);
  const core = useReplayReactionCore(drawn, { cur: v.cur, playing: v.playing });
  const [card, setCard] = useState<number | null>(null);
  const [now, setNow] = useState("");
  const track = useRef<HTMLDivElement>(null);
  const tick = p.tick;
  const total = v.total || p.reactions?.lengthSec || talk.duration_sec || 1;
  const bars = useMemo(() => (p.reactions ? stripBars(p.reactions.live, core.replay) : []), [p.reactions, core.replay]);
  // Why kept to the length: a mark past the end of what plays would sit off the bar.
  const pins = (p.reactions?.moments ?? []).filter((m) => m.at <= total).slice(0, PINS);
  const started = v.playing || v.cur > 0;
  // The workspace's measurement hears the replay play, as it hears any talk.
  const second = Math.floor(v.cur);
  useEffect(() => {
    tick({ to: second, playing: v.playing });
  }, [second, v.playing, tick]);
  return (
    <div className="rp">
      <div className="rp-pic">
        <video
          ref={v.videoRef}
          playsInline
          preload="auto"
          poster={talk.poster_url ?? undefined}
          {...v.events}
        />
        <Floats lane={core.lane} cap={10} />
        {started ? null : (
          <div className="rp-big">
            <button className="playc" type="button" aria-label="Play the replay" onClick={v.play}>
              <GIcon name="play" size={30} />
            </button>
          </div>
        )}
      </div>
      <div className="rp-ctl">
        <button className="ibtn" type="button" aria-label={v.playing ? "Pause the replay" : "Play the replay"} onClick={v.toggle}>
          <GIcon name={v.playing ? "pause" : "play"} size={22} />
        </button>
        <span className="rp-time tnum">
          <b>{mmss(v.cur)}</b> / {mmss(total)}
        </span>
        <div
          className="rp-track"
          ref={track}
          role="slider"
          aria-label="Replay position"
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.floor(v.cur)}
          tabIndex={0}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("[data-pin]")) return;
            const r = e.currentTarget.getBoundingClientRect();
            v.seek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * total);
            setNow("");
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") v.seek(v.cur + 5);
            if (e.key === "ArrowLeft") v.seek(v.cur - 5);
          }}
        >
          <div className="rp-strip" aria-hidden="true">
            {bars.map((b, i) => (
              <i key={i} style={{ height: `${b.h}%` }} className={b.pale ? "pale" : undefined} />
            ))}
          </div>
          <div className="rp-line">
            <i style={{ width: `${Math.min(100, (v.cur / total) * 100)}%` }} />
          </div>
          {pins.map((m, i) => {
            const mix = addCounts(m.live, m.replay);
            const top = byCount(mix)[0]?.[0] ?? "love";
            return (
              <span key={m.at} className="rp-pin" style={{ left: `${((m.at / total) * 100).toFixed(2)}%` }}>
                <button
                  type="button"
                  data-pin={i}
                  aria-label={`The moment at ${mmss(m.at)}`}
                  onPointerEnter={() => setCard(i)}
                  onPointerLeave={() => setCard(null)}
                  onFocus={() => setCard(i)}
                  onBlur={() => setCard(null)}
                  onClick={() => {
                    v.seek(Math.max(0, m.at - PLAY_BEFORE_SEC));
                    v.play();
                    setNow(`Playing the moment at ${mmss(m.at)}${m.chapter ? `, ${m.chapter}` : ""}`);
                  }}
                >
                  {REACTION_OF[top].emoji}
                  <span className="pt"> {mmss(m.at)}</span>
                </button>
              </span>
            );
          })}
          {card !== null && pins[card] ? (
            <div className="rp-card" style={{ left: `${((pins[card].at / total) * 100).toFixed(2)}%` }}>
              <b>
                {mmss(pins[card].at)}
                {pins[card].chapter ? ` · ${pins[card].chapter}` : ""}
              </b>
              <span>
                {byCount(addCounts(pins[card].live, pins[card].replay))
                  .slice(0, 2)
                  .map(([k, n]) => `${REACTION_OF[k].emoji} ${fmt(n)}`)
                  .join(" · ")}{" "}
                during the live
              </span>
              {pins[card].words ? <q>{pins[card].words}</q> : null}
            </div>
          ) : null}
        </div>
      </div>
      <p className="rp-now">{now}</p>
      <ReactionRow onTap={core.tap} />
    </div>
  );
}

/** The seven reactions under the replay; before it plays they still rise, with nothing yet to keep them on. */
function ReactionRow({ onTap }: { onTap: (kind: (typeof REACTIONS)[number]["kind"]) => void }): ReactNode {
  return (
    <div className="rp-reacts" aria-label="React to the replay">
      {REACTIONS.map((r) => (
        <button key={r.kind} type="button" onClick={() => onTap(r.kind)}>
          <span>{r.emoji}</span>
          {r.label}
        </button>
      ))}
    </div>
  );
}

/** The replay's place while it is prepared: the picture and the reactions. Nothing to play yet, so no player. */
function ReplayWaiting({ talk, coming }: { talk: WatchSession; coming: boolean }): ReactNode {
  const core = useReplayReactionCore(undefined, { cur: 0, playing: false });
  return (
    <div className="rp">
      <div className="rp-pic">
        {talk.poster_url ? <img src={talk.poster_url} alt="" aria-hidden="true" /> : null}
        <Floats lane={core.lane} cap={10} />
        <div className="rp-prep">
          <b>{coming ? "The replay will be here in a few minutes" : "The live has ended."}</b>
          {coming ? (
            <>
              <div className="rp-shim">
                <i />
              </div>
              <p>It will play right here, on this page.</p>
            </>
          ) : null}
        </div>
      </div>
      {coming ? <ReactionRow onTap={core.tap} /> : null}
    </div>
  );
}

export function LiveAfter(p: AfterProps): ReactNode {
  const [chatOpen, setChatOpen] = useState(false);
  const { toast, say } = useToast();
  const keep = useKeepTalking(p.token, p.talk.webinar_id, p.circleId, p.viewerFirst);
  const together = keep ? talkingWords(keep.faces.slice(0, 2).map((f) => f.name.split(" ")[0]), keep.others) : null;
  return (
    <div className="end">
      <div className="end-bg" />
      <header className="end-top">
        <Brand brand={p.brand} href={p.yourPage} />
        <a className="top-link" href={p.yourPage}>
          <GIcon name="home" size={16} /> Your page
        </a>
      </header>
      <div className="end-wrap">
        <div>
          <div className="end-h">
            <p className="eyebrow">{p.eyebrow}</p>
            <h1 className="h-1">Thank you for coming, {p.viewerFirst}.</h1>
          </div>
          {p.replay === "ready" && p.talk.playback_url ? (
            <>
              <Replay {...p} />
            </>
          ) : (
            <>
              <ReplayWaiting talk={p.talk} coming={p.replay !== "none" || p.recorded} />
            </>
          )}
          <div className="card keep">
            <div>
              <h3>Keep talking</h3>
              <p className="muted">
                Ask {p.hostFirst} anything about tonight, just between you two.
                {keep ? ` Or join the ${keep.noun}’s chat${together ? `, where ${together} ${keep.faces.length + keep.others === 1 ? "is" : "are"} still talking` : ""}.` : ""}
              </p>
            </div>
            <div className="keep-b">
              <a className="btn btn-p" href={`${p.yourPage}?at=direct`}>
                Message {p.hostFirst}
              </a>
              {keep ? (
                <a className="btn btn-g" href={`${p.yourPage}?at=${keep.at}`}>
                  {keep.faces.length > 0 ? (
                    <span className="keep-faces">
                      {keep.faces.map((f) =>
                        f.photoUrl ? <img key={f.name} src={f.photoUrl} alt="" /> : <span key={f.name} className="av av-i">{f.name.charAt(0)}</span>,
                      )}
                    </span>
                  ) : null}
                  Open the {keep.noun}’s chat
                </a>
              ) : null}
            </div>
          </div>
        </div>
        <aside className="end-side">
          {p.offer ? (
            <div className="end-offer">
              <div className="oc-h">
                {p.talk.host_avatar_url ? <img className="av" src={p.talk.host_avatar_url} alt="" /> : null}
                <div>
                  <b>{p.hostFirst}’s offer from tonight</b>
                </div>
              </div>
              {p.offer.text ? <h3>{p.offer.text}</h3> : null}
              <OfferPress offer={p.offer} host={p.hostFirst} viewerEmail={p.viewerEmail} className="btn btn-p btn-w" onConvert={p.onConvert} onChat={() => setChatOpen(true)}>
                {offerPill(p.offer, p.hostFirst)} <GIcon name="arrowr" size={18} />
              </OfferPress>
            </div>
          ) : null}
          {p.next ? (
            <div className="card nx">
              <div className="nx-date">
                <span className="nx-cal">
                  <span>{p.next.weekday}</span>
                  <b>{p.next.day}</b>
                </span>
                <div>
                  <p className="eyebrow" style={{ marginBottom: 4 }}>
                    {p.next.when}
                  </p>
                  <h3>{p.next.title}</h3>
                </div>
              </div>
              <p style={{ fontSize: "var(--t-text)", color: "var(--ink-2)", display: "flex", gap: 8, alignItems: "center" }}>
                <GIcon name="check" size={16} /> Your seat is saved
              </p>
              {p.calendar ? <CalendarButtons links={p.calendar} onUsed={() => say("The next session is in your calendar.", "cal")} /> : null}
            </div>
          ) : null}
        </aside>
      </div>
      <footer className="foot" style={{ paddingBottom: 36 }}>
        <span>
          © {new Date().getFullYear()} {p.brand.workspace_name}
        </span>
        <Made />
      </footer>
      {chatOpen ? (
        <Sheet label={`Chat with ${p.hostFirst}`} tall onClose={() => setChatOpen(false)}>
          <RoomChat session={p.talk} thread={p.thread} host={p.hostFirst} onClose={() => setChatOpen(false)} />
        </Sheet>
      ) : null}
      <Toast toast={toast} />
    </div>
  );
}
